import React, { useState, useRef } from 'react';
import { useAuth } from './AuthContext';
import { T, FormCard, Toast } from './theme';

const API_BASE = 'https://d3bdqxkpn753kf.cloudfront.net';

const CreateEventSection = () => {
  const { role } = useAuth();
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [form, setForm] = useState({ title: '', event_start_date: '', event_end_date: '', event_details: '', visibility: 'PUBLIC' });
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const roles = (role || '').split(',').map(s => s.trim().toUpperCase());
  const isAdmin = roles.includes('ADMIN');
  const canDirectPublish = isAdmin || (roles.includes('CONTENT-CREATOR') && roles.includes('CONTENT-APPROVER'));

  const handleStartDateChange = (e) => {
    const startDate = e.target.value;
    const endDate = startDate ? startDate.slice(0, 10) + 'T23:59' : '';
    setForm({ ...form, event_start_date: startDate, event_end_date: endDate });
  };

  const showMsg = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.event_start_date || !form.event_end_date || !form.event_details) {
      showMsg('Please fill all required fields', 'error'); return;
    }
    setUploading(true);
    try {
      const token = localStorage.getItem('vrvToken');
      const createRes = await fetch(`${API_BASE}/vrv-events-create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, createdBy: localStorage.getItem('vrvEmail') || '', createdAt: new Date().toISOString(), status: 'APPROVED', visibility: form.visibility }),
      });
      const createData = await createRes.json();
      const parsed = typeof createData.body === 'string' ? JSON.parse(createData.body) : createData;
      if (!createRes.ok) { showMsg(parsed.message || parsed.error || 'Failed to create event', 'error'); setUploading(false); return; }
      const eventId = parsed.event_id;
      if (imageFile && eventId) {
        const uploadUrlRes = await fetch(`${API_BASE}/vrv-events-generate-upload-url?event_id=${eventId}&fileName=${imageFile.name}`, { method: 'GET', headers: { Authorization: `Bearer ${token}` } });
        const uploadUrlData = await uploadUrlRes.json();
        const parsedUpload = typeof uploadUrlData.body === 'string' ? JSON.parse(uploadUrlData.body) : uploadUrlData;
        if (parsedUpload.uploadUrl) {
          await fetch(parsedUpload.uploadUrl, { method: 'PUT', headers: { 'Content-Type': imageFile.type }, body: imageFile });
          if (parsedUpload.imageUrl) {
            await fetch(`${API_BASE}/vrv-events-action`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ event_id: eventId, action: 'UPDATE_IMAGE', event_image_url: parsedUpload.imageUrl + '?v=' + Date.now() }) });
          }
        }
      }
      showMsg('Event created successfully!', 'success');
      setForm({ title: '', event_start_date: '', event_end_date: '', event_details: '', visibility: 'PUBLIC' });
      setImageFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch { showMsg('Error connecting to server', 'error'); }
    setUploading(false);
  };

  return (
    <div>
      <FormCard title="Create New Event" subtitle="Event Management">
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="md:col-span-2">
            <label className="vrv-label">Title *</label>
            <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder="Event title" className="vrv-input" required />
          </div>
          <div>
            <label className="vrv-label">Start Date *</label>
            <input type="datetime-local" value={form.event_start_date} onChange={handleStartDateChange}
              min={new Date().toISOString().slice(0, 10) + 'T00:00'} className="vrv-input" required />
          </div>
          <div>
            <label className="vrv-label">End Date</label>
            <input type="datetime-local" value={form.event_end_date} onChange={e => setForm({ ...form, event_end_date: e.target.value })}
              className="vrv-input" required />
          </div>
          <div className="md:col-span-2">
            <label className="vrv-label">Event Image</label>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={e => setImageFile(e.target.files[0])} className="vrv-input" />
            {imageFile && (
              <div className="mt-3 flex items-center gap-3 p-3 rounded-xl" style={{ background: '#F8F5EE', border: '1px solid rgba(0,0,0,0.07)' }}>
                <img src={URL.createObjectURL(imageFile)} alt="Preview" className="h-16 w-16 object-cover rounded-lg" />
                <div>
                  <p className="text-xs font-semibold" style={{ color: T.ink }}>{imageFile.name}</p>
                  <p className="text-xs" style={{ color: T.muted }}>{(imageFile.size / 1024).toFixed(1)} KB</p>
                </div>
                <button type="button" onClick={() => { setImageFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                  className="ml-auto text-xs font-bold" style={{ color: T.danger }}>✕ Remove</button>
              </div>
            )}
          </div>
          <div className="md:col-span-2">
            <label className="vrv-label">Event Details *</label>
            <textarea value={form.event_details} onChange={e => setForm({ ...form, event_details: e.target.value })}
              placeholder="Describe the event…" rows="5" className="vrv-input resize-none" required />
          </div>
          {canDirectPublish && (
            <div>
              <label className="vrv-label">Visibility</label>
              <select value={form.visibility} onChange={e => setForm({ ...form, visibility: e.target.value })} className="vrv-select">
                <option value="PUBLIC">🌐 Public</option>
                <option value="PRIVATE">🔒 Private</option>
              </select>
            </div>
          )}
          <div className={`flex items-end ${canDirectPublish ? '' : 'md:col-span-2'}`}>
            <button type="submit" disabled={uploading} className="btn-gold">
              {uploading ? 'Creating…' : canDirectPublish ? 'Create Event →' : 'Send for Approval →'}
            </button>
          </div>
        </form>
      </FormCard>
      <Toast toast={toast} onClose={() => setToast({ show: false, message: '', type: '' })} />
    </div>
  );
};

export default CreateEventSection;
